import React from 'react';
import Select from 'react-select';
import AdminUserPicker, { getUserLabel } from './AdminUserPicker';
import PrizeEditor, { PRIZE_TYPES } from './PrizeEditor';

// Panel de las ligas.
//
// Antes todo esto se hacia escribiendo "!games ..." en el chat de Twitch, incluido dar puntos.
// Una liga es el par (juego, subliga): el juego sale del catalogo de la pestaña "Juegos" y la
// subliga por defecto es "General", que es lo que eran todas las ligas de siempre. Cada subliga
// lleva su propia temporada y sus propios premios.
//
// Se divide en tres sub-pestañas (crear, gestionar, puntos) con el mismo patron que la barra de
// pestañas del panel. La lista de ligas y su refresh viven aqui arriba para no recargarla cada
// vez que se cambia de sub-pestaña.

const SUBLEAGUE_DEFAULT = 'General';

const TABS = [
    { id: 'create', label: 'Crear / editar liga' },
    { id: 'manage', label: 'Gestionar ligas' },
    { id: 'points', label: 'Puntos' },
];

// Tambien lo usa AdminMetricas: un selector oscuro igual en todo el panel.
export const selectStyles = {
    control: (provided) => ({ ...provided, backgroundColor: 'rgb(81, 84, 82)', borderColor: '#444', color: '#fff' }),
    menu: (provided) => ({ ...provided, backgroundColor: 'rgb(81, 84, 82)', zIndex: 30 }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isFocused ? 'rgba(255, 255, 255, 0.1)' : 'rgb(81, 84, 82)',
        color: '#fff',
        cursor: 'pointer',
    }),
    singleValue: (provided) => ({ ...provided, color: '#fff' }),
    input: (provided) => ({ ...provided, color: '#fff' }),
    placeholder: (provided) => ({ ...provided, color: '#ccc' }),
};

const post = async (query, fields) => {
    const body = new FormData();
    Object.keys(fields || {}).forEach(key => body.append(key, fields[key]));
    const res = await fetch('/api/adminLogin?' + query, { method: 'POST', body: body });
    let payload = null;
    try { payload = await res.json(); } catch (err) { /* respuesta sin cuerpo JSON */ }
    return { ok: res.ok, payload: payload };
};

const leagueLabel = (league) => league.gameName +
    (league.subLeague === SUBLEAGUE_DEFAULT ? '' : ' - ' + league.subLeague);

const leagueOption = (league) => ({ value: league.id, label: leagueLabel(league) + ' (T' + league.season + ')' });

// La liga elegida para dar puntos se recuerda en la pestaña del navegador: el panel desmonta
// este componente al cambiar de sección, y volver a buscarla cada vez era el pan de cada día.
const POINTS_LEAGUE_KEY = 'adminPointsLeague';

const AdminLigas = () => {
    const [tab, setTab] = React.useState('create');
    const [leagues, setLeagues] = React.useState([]);
    const [gameOptions, setGameOptions] = React.useState([]);
    const [rewardOptions, setRewardOptions] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [status, setStatus] = React.useState('');
    const [statusType, setStatusType] = React.useState(''); // 'success' | 'error' | ''

    // Alta / edicion
    const [editing, setEditing] = React.useState(null); // liga que se esta editando, o null = alta
    const [newGame, setNewGame] = React.useState(null);
    const [newSubLeague, setNewSubLeague] = React.useState('');
    const [prizes, setPrizes] = React.useState([]);
    const [defaultPrize, setDefaultPrize] = React.useState(null);

    // Puntos
    const [pointsLeague, setPointsLeague] = React.useState(null);
    const [platform, setPlatform] = React.useState('twitch');
    const [selectedUser, setSelectedUser] = React.useState(null);
    const [amount, setAmount] = React.useState(1);

    const choosePointsLeague = (option) => {
        setPointsLeague(option);
        try { sessionStorage.setItem(POINTS_LEAGUE_KEY, option ? option.value : ''); } catch (err) { }
    };

    const say = (text, type) => { setStatus(text); setStatusType(type); };

    const refresh = React.useCallback(async () => {
        try {
            const { payload } = await post('action=get&type=leagues');
            setLeagues(Array.isArray(payload) ? payload : []);
        } catch (err) {
            say('No se pudieron leer las ligas', 'error');
        }
    }, []);

    const loadOptions = React.useCallback(async () => {
        try {
            // Misma accion que alimenta el select de juegos del resto del panel.
            const games = await post('action=getGamesOption');
            setGameOptions(Array.isArray(games.payload) ? games.payload : []);
            const rewards = await post('action=getRewardsPrizeOption');
            setRewardOptions(Array.isArray(rewards.payload) ? rewards.payload : []);
        } catch (err) {
            say('No se pudo leer el catálogo de juegos o recompensas', 'error');
        }
    }, []);

    React.useEffect(() => { refresh(); loadOptions(); }, [refresh, loadOptions]);

    // Rehace la liga de puntos cada vez que llega la lista: al volver a esta pestaña la recupera
    // del sessionStorage, y de paso refresca su etiqueta (lleva la temporada, que cambia) o la
    // vacía si esa liga ya no existe.
    React.useEffect(() => {
        if (leagues.length === 0) return;
        let id = pointsLeague ? pointsLeague.value : '';
        if (!id) {
            try { id = sessionStorage.getItem(POINTS_LEAGUE_KEY) || ''; } catch (err) { }
        }
        const league = leagues.find(l => String(l.id) === String(id));
        setPointsLeague(league ? leagueOption(league) : null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leagues]);

    // Envoltorio comun: bloquea el doble click, pinta el resultado y recarga la lista.
    const run = async (query, fields, onOk) => {
        if (loading) return;
        setLoading(true);
        say('', '');
        try {
            const { ok, payload } = await post(query, fields);
            if (ok && payload && payload.success) {
                say(onOk(payload), 'success');
            } else {
                say((payload && payload.error) || 'No se pudo completar la operación', 'error');
            }
            await refresh();
        } catch (err) {
            say('Error de red', 'error');
        } finally {
            setLoading(false);
        }
    };

    // --- Alta / edicion ---

    const resetForm = () => {
        setEditing(null);
        setNewGame(null);
        setNewSubLeague('');
        setPrizes([]);
        setDefaultPrize(null);
    };

    // Carga una liga en el formulario. La llaman el selector de esta pestaña y el boton
    // "Editar premios" de la pestaña de gestionar.
    const startEdit = (league) => {
        if (!league) return;
        setEditing(league);
        setNewGame(gameOptions.find(g => String(g.value) === String(league.gameDefId)) || null);
        setNewSubLeague(league.subLeague === SUBLEAGUE_DEFAULT ? '' : league.subLeague);
        setPrizes((league.prizes || []).map(p => ({
            kind: p.kind,
            rewardId: p.rewardId || '',
            amount: p.amount || 1
        })));
        setDefaultPrize(league.defaultPrize ? {
            kind: league.defaultPrize.kind,
            rewardId: league.defaultPrize.rewardId || '',
            amount: league.defaultPrize.amount || 1
        } : null);
        say('Editando "' + leagueLabel(league) + '". Cambia sus premios y pulsa Guardar cambios.', 'success');
    };

    const loadForEdit = (option) => {
        if (!option) { resetForm(); return; }
        startEdit(leagues.find(l => l.id === option.value));
    };

    // La posicion sale del orden de la lista: Top 1 es la primera fila.
    const serializePrizes = () => JSON.stringify(
        prizes.map((p, i) => ({ position: i + 1, kind: p.kind, rewardId: p.rewardId, amount: p.amount }))
    );

    const handleSave = () => {
        if (!newGame) {
            say('Elige el juego al que pertenece la liga', 'error');
            return;
        }
        const faltaRecompensa = prizes.concat(defaultPrize ? [defaultPrize] : [])
            .some(p => p.kind === 'reward' && !p.rewardId);
        if (faltaRecompensa) {
            say('Hay un premio de tipo Recompensa sin recompensa elegida', 'error');
            return;
        }

        const sub = newSubLeague.trim() || SUBLEAGUE_DEFAULT;
        const fields = {
            gameDefId: newGame.value,
            subLeague: newSubLeague.trim(),
            prizes: serializePrizes(),
            defaultPrize: defaultPrize ? JSON.stringify(defaultPrize) : ''
        };

        if (editing) {
            run('action=edit&type=leagues', Object.assign({ id: editing.id }, fields),
                () => 'Liga "' + newGame.label + (sub === SUBLEAGUE_DEFAULT ? '' : ' - ' + sub) + '" guardada.');
            return;
        }

        run('action=create&type=leagues', fields, () => {
            resetForm();
            return 'Liga "' + newGame.label + (sub === SUBLEAGUE_DEFAULT ? '' : ' - ' + sub) + '" creada.';
        });
    };

    // --- Gestion ---

    const handleRename = (league) => {
        const nuevo = window.prompt(
            'Nombre de la subliga dentro de ' + league.gameName + ' (vacío = ' + SUBLEAGUE_DEFAULT + '):',
            league.subLeague
        );
        if (nuevo === null) return;
        // Sin mandar prizes: el backend deja la configuración de premios como está.
        run(
            'action=edit&type=leagues',
            { id: league.id, gameDefId: league.gameDefId, subLeague: nuevo.trim() },
            () => 'Liga renombrada a "' + (nuevo.trim() || SUBLEAGUE_DEFAULT) + '".'
        );
    };

    const handleDelete = (league) => {
        if (!window.confirm(
            '¿Borrar la liga "' + leagueLabel(league) + '"?\n\n' +
            'Se pierde TODO su histórico de puntos, de todas las temporadas. No tiene vuelta atrás.'
        )) return;
        run('action=delete&type=leagues', { id: league.id }, () => 'Liga borrada.');
    };

    const handleNewSeason = (league) => {
        const aviso = league.prizesPaid
            ? ''
            : '\n\nOJO: los premios de la temporada ' + league.season + ' todavía NO se han entregado.';
        if (!window.confirm(
            '¿Empezar la temporada ' + (league.season + 1) + ' de "' + leagueLabel(league) + '"?\n\n' +
            'La clasificación arranca de cero; las temporadas anteriores se siguen pudiendo consultar en la web.' +
            aviso
        )) return;
        run('action=leaguesControl', { op: 'newSeason', id: league.id },
            (data) => 'Empieza la temporada ' + data.season + ' de ' + leagueLabel(league) + '.');
    };

    const handlePayPrizes = (league) => {
        if (!window.confirm(
            '¿Entregar los premios de la temporada ' + league.season + ' de "' + leagueLabel(league) + '"?\n\n' +
            'Van a cobrar los ' + league.players + ' participantes con victorias, según la clasificación ' +
            'que se ve ahora mismo en /games. Solo se puede hacer una vez por temporada.'
        )) return;
        run('action=leaguesControl', { op: 'payPrizes', id: league.id }, (data) => (
            'Premios entregados a ' + data.paid + ' participante(s) de la temporada ' + data.season + '.' +
            (data.skipped && data.skipped.length > 0
                ? ' Se han saltado: ' + data.skipped.join(', ') + '.'
                : '')
        ));
    };

    // Interruptor: pulsar en la que ya esta activa la apaga y deja el overlay vacio.
    const handleOverlay = (league) => {
        run('action=leaguesControl', { op: 'setOverlay', id: league.id }, (data) => (
            data.overlay
                ? leagueLabel(league) + ' se está mostrando en el overlay de OBS.'
                : 'Overlay vacío: ya no se muestra ninguna liga.'
        ));
    };

    const handleToggle = (league, op) => {
        run('action=leaguesControl', { op: op, id: league.id }, (data) => (
            op === 'toggleStatus'
                ? leagueLabel(league) + (data.status ? ' activada (sale en /games).' : ' desactivada (oculta en /games).')
                : leagueLabel(league) + (data.onProgress ? ' se muestra en la portada.' : ' ya no sale en la portada.')
        ));
    };

    // --- Puntos ---

    const handlePoints = (op) => {
        if (!pointsLeague) {
            say('Elige la liga', 'error');
            return;
        }
        if (!selectedUser) {
            say('Busca y elige un usuario', 'error');
            return;
        }
        const nombre = getUserLabel(selectedUser, platform);
        run(
            'action=leaguesControl',
            // Por _id: los puntos no dependen de tener Twitch enlazado.
            { op: op, id: pointsLeague.value, userId: selectedUser._id, amount: amount },
            (data) => (op === 'addPoints'
                ? 'Sumadas ' + amount + ' victoria(s) a ' + nombre + ' (total temporada ' + data.season + ': ' + data.points + ').' +
                  ' Se le han dado ' + data.overbox + ' overbox.' +
                  (data.referido ? ' Además, ' + data.referido + ' se lleva 20 birux comodín por haberle invitado.' : '')
                : 'Quitadas ' + data.removed + ' victoria(s) a ' + nombre + ' (total temporada ' + data.season + ': ' + data.points + ').')
        );
    };

    // Resumen de los premios de una liga, para verlos en la lista sin abrir el formulario.
    // El nombre de las recompensas sale del catálogo ya cargado, que la lista no trae.
    const prizeText = (prize) => {
        if (!prize || !prize.kind) return '';
        if (prize.kind === 'reward') {
            const option = rewardOptions.find(o => o.value === String(prize.rewardId));
            const nombre = option ? option.label : 'recompensa borrada';
            return prize.amount > 1 ? prize.amount + 'x ' + nombre : nombre;
        }
        const tipo = PRIZE_TYPES.find(t => t.value === prize.kind);
        return prize.amount + ' ' + (tipo ? tipo.label : prize.kind);
    };

    const prizeSummary = (league) => {
        const partes = (league.prizes || []).map(p => 'Top ' + p.position + ': ' + prizeText(p));
        if (league.defaultPrize) partes.push('Todos: ' + prizeText(league.defaultPrize));
        return partes.join(' · ');
    };

    // Agrupadas por juego, con la General primero: es el orden en el que las devuelve el backend.
    const porJuego = leagues.reduce((acc, league) => {
        (acc[league.gameName] = acc[league.gameName] || []).push(league);
        return acc;
    }, {});

    const leagueOptions = leagues.map(leagueOption);

    return (
        <div className='heroes formOver'>
            <h1>Ligas</h1>

            <div className='navTops heroes unselectable'>
                {TABS.map(t => (
                    <div
                        key={t.id}
                        className={tab === t.id ? 'active' : ''}
                        onClick={() => { setTab(t.id); say('', ''); }}
                    >
                        {t.label}
                    </div>
                ))}
            </div>

            {tab === 'create' ? (
                <>
                    <h2>{editing ? 'Editar liga' : 'Crear liga'}</h2>
                    <div className='formFieldRow'>
                        <label className='formFieldLabel heroes'>Editar una liga existente</label>
                        <Select
                            className='heroes selectForm'
                            options={leagueOptions}
                            isSearchable
                            isClearable
                            styles={selectStyles}
                            placeholder='(dejar vacío para crear una nueva)'
                            value={editing ? leagueOptions.find(o => o.value === editing.id) || null : null}
                            onChange={loadForEdit}
                        />
                    </div>
                    <div className='formFieldRow'>
                        <label className='formFieldLabel heroes'>Juego</label>
                        <Select
                            className='heroes selectForm'
                            options={gameOptions}
                            isSearchable
                            styles={selectStyles}
                            placeholder='Elige el juego'
                            value={newGame}
                            onChange={setNewGame}
                        />
                    </div>
                    <div className='formFieldRow'>
                        <label className='formFieldLabel heroes'>Subliga</label>
                        <input
                            className='userSearch'
                            placeholder={SUBLEAGUE_DEFAULT}
                            value={newSubLeague}
                            onChange={(e) => setNewSubLeague(e.target.value)}
                        />
                    </div>
                    <p style={{ fontSize: 13, color: '#ccc' }}>
                        Déjalo vacío para la liga <strong>{SUBLEAGUE_DEFAULT}</strong> del juego. Ponle nombre
                        (por ejemplo <em>Balón Brawl</em>) para abrir una subliga dentro del mismo juego: comparte
                        juego pero lleva su propia clasificación, sus propias temporadas y sus propios premios.
                    </p>

                    <PrizeEditor
                        prizes={prizes}
                        defaultPrize={defaultPrize}
                        rewardOptions={rewardOptions}
                        onChangePrizes={setPrizes}
                        onChangeDefault={setDefaultPrize}
                    />

                    <p style={{ fontSize: 13, color: '#ccc' }}>
                        Los premios se guardan en la liga y valen para todas sus temporadas hasta que los cambies.
                        Se entregan desde <strong>Gestionar ligas</strong>, con el botón de entregar premios.
                    </p>

                    <div className='navTops heroes unselectable'>
                        <div onClick={() => { if (!loading) { handleSave(); } }}>
                            {editing ? 'Guardar cambios' : 'Crear liga'}
                        </div>
                        {editing ? <div onClick={() => { resetForm(); say('', ''); }}>Cancelar</div> : null}
                    </div>
                </>
            ) : null}

            {tab === 'manage' ? (
                <>
                    <h2>Ligas existentes</h2>
                    {leagues.length === 0 ? <p>Todavía no hay ninguna liga.</p> : (
                        <div>
                            {Object.keys(porJuego).map(gameName => (
                                <div key={gameName} style={{ marginBottom: 18 }}>
                                    <h3 style={{ marginBottom: 4 }}>{gameName}</h3>
                                    {porJuego[gameName].map(league => (
                                        <div key={league.id} className='formFieldRow prizeRow'>
                                            <span style={{ flex: 1, minWidth: 240, textAlign: 'left' }}>
                                                <strong>{league.subLeague}</strong>
                                                {league.subLeague === SUBLEAGUE_DEFAULT ? ' (liga general del juego)' : ''}
                                                <br />
                                                <span style={{ fontSize: 13, color: '#ccc' }}>
                                                    {'Temporada ' + league.season + ' · ' + league.players + ' participantes'}
                                                    {league.status ? '' : ' · OCULTA en /games'}
                                                    {league.onProgress ? ' · en portada' : ''}
                                                    {league.overlay ? ' · EN OVERLAY' : ''}
                                                    {(league.prizes.length > 0 || league.defaultPrize)
                                                        ? (league.prizesPaid ? ' · premios ENTREGADOS' : ' · premios pendientes')
                                                        : ' · sin premios configurados'}
                                                </span>
                                                {(league.prizes.length > 0 || league.defaultPrize) ? (
                                                    <>
                                                        <br />
                                                        <span style={{ fontSize: 13, color: '#9fb' }}>{prizeSummary(league)}</span>
                                                    </>
                                                ) : null}
                                            </span>
                                            <div
                                                className='miniBtn heroes unselectable'
                                                onClick={() => { startEdit(league); setTab('create'); }}
                                            >
                                                Editar premios
                                            </div>
                                            <div className='miniBtn heroes unselectable' onClick={() => { if (!loading) { handleOverlay(league); } }}>
                                                {league.overlay ? 'Quitar del overlay' : 'Mostrar en overlay'}
                                            </div>
                                            <div className='miniBtn heroes unselectable' onClick={() => { if (!loading) { handlePayPrizes(league); } }}>Entregar premios</div>
                                            <div className='miniBtn heroes unselectable' onClick={() => { if (!loading) { handleNewSeason(league); } }}>Nueva temporada</div>
                                            <div className='miniBtn heroes unselectable' onClick={() => { if (!loading) { handleToggle(league, 'toggleStatus'); } }}>
                                                {league.status ? 'Desactivar' : 'Activar'}
                                            </div>
                                            <div className='miniBtn heroes unselectable' onClick={() => { if (!loading) { handleToggle(league, 'toggleOnProgress'); } }}>
                                                {league.onProgress ? 'Quitar de portada' : 'Mostrar en portada'}
                                            </div>
                                            <div className='miniBtn heroes unselectable' onClick={() => { if (!loading) { handleRename(league); } }}>Renombrar</div>
                                            <div className='miniBtn heroes unselectable' onClick={() => { if (!loading) { handleDelete(league); } }}>Borrar</div>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    )}
                    <p style={{ fontSize: 13, color: '#ccc' }}>
                        Entregar premios y pasar de temporada son dos acciones independientes: entrega primero y
                        pasa de temporada después. Los premios de una temporada solo se pueden entregar una vez.
                    </p>
                </>
            ) : null}

            {tab === 'points' ? (
                <>
                    <h2>Puntos</h2>
                    <div className='formFieldRow'>
                        <label className='formFieldLabel heroes'>Liga</label>
                        <Select
                            className='heroes selectForm'
                            options={leagueOptions}
                            isSearchable
                            styles={selectStyles}
                            placeholder='Elige la liga'
                            value={pointsLeague}
                            onChange={choosePointsLeague}
                        />
                    </div>
                    <AdminUserPicker
                        platform={platform}
                        onPlatformChange={(p) => { setPlatform(p); setSelectedUser(null); }}
                        selectedUser={selectedUser}
                        onSelectUser={setSelectedUser}
                    />
                    <div className='formFieldRow'>
                        <label className='formFieldLabel heroes'>Victorias</label>
                        <input
                            className='userSearch'
                            type='number'
                            min='1'
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            onWheel={(e) => e.currentTarget.blur()}
                        />
                    </div>
                    <p style={{ fontSize: 13, color: '#ccc' }}>
                        Los puntos van a la <strong>temporada abierta</strong> de la liga elegida. Cada victoria da
                        <strong> 1 overbox</strong> al ganador, y la primera victoria de un referido da 20 birux
                        comodín al que le invitó. Quitar puntos <strong>no</strong> retira las overbox ya dadas:
                        sirve para corregir una pulsación de más, no para deshacer la recompensa.
                    </p>
                    <div className='navTops heroes unselectable'>
                        <div onClick={() => { if (!loading) { handlePoints('addPoints'); } }}>Dar puntos</div>
                        <div onClick={() => { if (!loading) { handlePoints('removePoints'); } }}>Quitar puntos</div>
                    </div>
                </>
            ) : null}

            {loading ? <p>Cargando...</p> : null}
            {status ? (
                <p
                    role={statusType === 'error' ? 'alert' : 'status'}
                    style={{
                        padding: '8px 12px',
                        borderRadius: 4,
                        fontWeight: 600,
                        color: statusType === 'success' ? '#0a6b0a' : statusType === 'error' ? '#8a1a1a' : 'inherit',
                        backgroundColor: statusType === 'success' ? '#d9f5d9' : statusType === 'error' ? '#fdecea' : 'transparent'
                    }}
                >
                    {status}
                </p>
            ) : null}
        </div>
    );
};

export default AdminLigas;
